<template>
  <b-card class="bank-info-card">
    <div class="section-head">
      <h5>My Banks</h5>
      <span class="count-pill" :class="atLimit ? 'is-full' : ''">
        {{ myBanks.length }} / {{ maxAccounts }}
      </span>
    </div>

    <!-- MY BANKS -->
    <div v-if="isLoading" class="text-center py-4">
      <b-spinner label="Loading..."></b-spinner>
    </div>
    <template v-else>
      <div v-if="!myBanks.length" class="empty-box">
        You have not added a bank account yet. Use "Add a Bank" below.
      </div>

      <div class="bank-row" v-for="(b, i) in myBanks" :key="'mb' + i">
        <img class="bank-logo" :src="logoOf(b)" :alt="bankNameOf(b)" @error="onLogoError" />
        <div class="bank-main">
          <div class="bank-name">
            {{ bankNameOf(b) }}
            <span class="type-pill" :class="isPrimary(b) ? 'is-primary' : 'is-secondary'">
              {{ isPrimary(b) ? 'Primary' : 'Secondary' }}
            </span>
          </div>
          <div class="bank-sub">
            {{ maskAccount(b.account_number) }}
            <template v-if="b.branch_name"> &middot; {{ b.branch_name }}</template>
          </div>
        </div>
        <div class="bank-actions">
          <a href="#" class="act" @click.prevent="openDetail(b)">Details</a>
          <a href="#" class="act" v-if="!isPrimary(b)" @click.prevent="askMakePrimary(b)">Make Primary</a>
          <a href="#" class="act danger" @click.prevent="askRemove(b)">Remove</a>
        </div>
      </div>
    </template>

    <!-- ADD A BANK : searchable, replaces the old grid -->
    <div class="section-head mt-4">
      <h5>Add a Bank</h5>
    </div>
    <div v-if="atLimit" class="empty-box">
      You have reached the maximum of {{ maxAccounts }} bank accounts.
      Remove one before adding another.
    </div>
    <template v-else>
      <b-form-input
        v-model.trim="bankSearch"
        type="search"
        placeholder="Search bank by name, country or SWIFT code..."
        autocomplete="off"
      ></b-form-input>

      <div class="search-results" v-if="bankSearch">
        <div v-if="!matchedBanks.length" class="empty-box small">
          No bank matches "{{ bankSearch }}".
          <a href="#" @click.prevent="openRequestBank">Request a new bank</a>
        </div>
        <div
          class="result-row"
          v-for="(ab, i) in matchedBanks"
          :key="'ab' + i"
          @click="chooseBank(ab)"
        >
          <img class="bank-logo sm" :src="adminLogoOf(ab)" :alt="ab.bank_name" @error="onLogoError" />
          <div class="bank-main">
            <div class="bank-name">{{ ab.bank_name }}</div>
            <div class="bank-sub">
              {{ ab.country }}
              <template v-if="ab.swift_code"> &middot; {{ ab.swift_code }}</template>
            </div>
          </div>
          <span class="act">Select</span>
        </div>
        <div class="muted small mt-1" v-if="matchedBanks.length === searchLimit">
          Showing the first {{ searchLimit }} matches - refine your search for more.
        </div>
      </div>
      <div class="muted small mt-2" v-else>
        Start typing to find your bank.
        <a href="#" @click.prevent="openRequestBank">Bank not listed?</a>
      </div>
    </template>

    <!-- ADD ACCOUNT FORM -->
    <b-modal id="bank-add-modal" centered :title="'Add ' + (selectedBank.bank_name || 'Bank')">
      <div class="recipient-box" v-if="selectedBank.bank_name">
        <img class="bank-logo" :src="adminLogoOf(selectedBank)" @error="onLogoError" />
        <div>
          <div class="bank-name">{{ selectedBank.bank_name }}</div>
          <div class="bank-sub">{{ selectedBank.country }}</div>
        </div>
      </div>
      <div class="form-row-x">
        <label>Account Holder Name *</label>
        <b-form-input v-model.trim="form.account_holder_name"></b-form-input>
        <span class="text-danger" v-if="errors.account_holder_name">{{ errors.account_holder_name }}</span>
      </div>
      <div class="form-row-x">
        <label>Account Number *</label>
        <b-form-input v-model.trim="form.account_number"></b-form-input>
        <span class="text-danger" v-if="errors.account_number">{{ errors.account_number }}</span>
      </div>
      <div class="row">
        <div class="col-6 form-row-x">
          <label>Branch Name</label>
          <b-form-input v-model.trim="form.branch_name"></b-form-input>
        </div>
        <div class="col-6 form-row-x">
          <label>Branch Code</label>
          <b-form-input v-model.trim="form.branch_code"></b-form-input>
        </div>
      </div>
      <div class="form-row-x">
        <label>Account Type</label>
        <b-form-select v-model="form.bank_type" :options="bankTypeOptions"></b-form-select>
        <small class="muted" v-if="form.bank_type === 'Primary' && currentPrimary">
          {{ currentPrimary.bank && currentPrimary.bank.bank_name }} is currently Primary.
          Adding this as Primary may leave two Primary accounts - use "Make Primary" on the
          list instead if you want to switch.
        </small>
      </div>
      <div class="text-danger" v-if="formError">{{ formError }}</div>
      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="success" :disabled="isSaving" @click="saveBank">
          <b-spinner small v-if="isSaving" class="mr-1"></b-spinner>
          {{ isSaving ? "Saving..." : "Add Account" }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Cancel</b-button>
      </template>
    </b-modal>

    <!-- DETAIL -->
    <b-modal id="bank-detail-modal" centered title="Bank Account Details" hide-footer>
      <div v-if="detailLoading" class="text-center py-4"><b-spinner></b-spinner></div>
      <div v-else-if="detail && detail.account_number">
        <div class="recipient-box">
          <img class="bank-logo" :src="logoOf(detail)" @error="onLogoError" />
          <div>
            <div class="bank-name">{{ bankNameOf(detail) }}</div>
            <div class="bank-sub">{{ detail.bank && detail.bank.country }}</div>
          </div>
        </div>
        <div class="detail-row" v-for="(f, i) in detailRows" :key="'dr' + i">
          <span>{{ f.label }}</span><strong>{{ f.value || '-' }}</strong>
        </div>
        <p class="muted small mt-3">
          Bank details cannot be edited. To change anything, remove this account and add it again.
        </p>
      </div>
    </b-modal>

    <!-- REMOVE -->
    <b-modal id="bank-remove-modal" centered title="Remove Bank Account">
      <p v-if="target">
        Remove <strong>{{ bankNameOf(target) }}</strong>
        ({{ maskAccount(target.account_number) }}) from your account?
      </p>
      <div class="text-danger" v-if="formError">{{ formError }}</div>
      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="danger" :disabled="isSaving" @click="doRemove">
          {{ isSaving ? "Removing..." : "Yes, Remove" }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Keep</b-button>
      </template>
    </b-modal>

    <!-- MAKE PRIMARY (create-then-delete, since there is no update endpoint) -->
    <b-modal id="bank-primary-modal" centered title="Make Primary">
      <p v-if="target">
        Set <strong>{{ bankNameOf(target) }}</strong> as your Primary account?
      </p>
      <p class="muted small">
        There is no edit endpoint, so this re-creates the account as Primary and then removes
        the old record. Your details are preserved.
        <template v-if="currentPrimary">
          {{ bankNameOf(currentPrimary) }} will be changed to Secondary the same way.
        </template>
      </p>
      <div class="text-danger" v-if="formError">{{ formError }}</div>
      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="success" :disabled="isSaving" @click="doMakePrimary">
          <b-spinner small v-if="isSaving" class="mr-1"></b-spinner>
          {{ isSaving ? "Updating..." : "Confirm" }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Cancel</b-button>
      </template>
    </b-modal>

    <!-- REQUEST A NEW BANK -->
    <b-modal id="bank-request-modal" centered title="Request a New Bank">
      <p class="muted small">
        Cannot find your bank? Send us the details and we will review adding it.
      </p>
      <div class="form-row-x">
        <label>Bank Name *</label>
        <b-form-input v-model.trim="requestForm.bank_name"></b-form-input>
        <span class="text-danger" v-if="errors.bank_name">{{ errors.bank_name }}</span>
      </div>
      <div class="row">
        <div class="col-6 form-row-x">
          <label>Country *</label>
          <b-form-input v-model.trim="requestForm.country"></b-form-input>
          <span class="text-danger" v-if="errors.country">{{ errors.country }}</span>
        </div>
        <div class="col-6 form-row-x">
          <label>SWIFT Code</label>
          <b-form-input v-model.trim="requestForm.swift_code"></b-form-input>
        </div>
      </div>
      <div class="form-row-x">
        <label>Bank Address</label>
        <b-form-input v-model.trim="requestForm.bank_address"></b-form-input>
      </div>
      <div class="text-danger" v-if="formError">{{ formError }}</div>
      <template #modal-footer="{ cancel }">
        <b-button size="sm" variant="success" :disabled="isSaving" @click="sendBankRequest">
          {{ isSaving ? "Sending..." : "Send Request" }}
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">Cancel</b-button>
      </template>
    </b-modal>

    <b-toast id="bank-toast" variant="success" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Amej Pay</strong>
      </div>
      <div><p>{{ infoText }}</p></div>
    </b-toast>
    <b-toast id="bank-error-toast" variant="danger" solid append-toast toaster="b-toaster-top-center">
      <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
        <strong class="mr-auto">Error</strong>
      </div>
      <div><p>{{ formError }}</p></div>
    </b-toast>
  </b-card>
</template>

<script>
import { mapActions, mapState } from "vuex";
import fallbackLogo from "@/assets/amej-pay.png";

export default {
  name: "BankInfo",
  data() {
    return {
      isLoading: false,
      isSaving: false,
      detailLoading: false,
      bankSearch: "",
      searchLimit: 8,
      maxAccounts: 5,
      selectedBank: {},
      target: null,
      infoText: "",
      formError: "",
      errors: {},
      fallbackLogo: fallbackLogo,
      form: {
        account_holder_name: "",
        account_number: "",
        branch_name: "",
        branch_code: "",
        bank_type: "Secondary",
      },
      requestForm: { bank_name: "", country: "", swift_code: "", bank_address: "" },
      bankTypeOptions: [
        { value: "Secondary", text: "Secondary" },
        { value: "Primary", text: "Primary" },
      ],
    };
  },
  computed: {
    ...mapState(["adminBanks", "userBanks", "userBankDetail", "profile"]),
    myBanks: function () {
      return Array.isArray(this.userBanks) ? this.userBanks : [];
    },
    allAdminBanks: function () {
      return Array.isArray(this.adminBanks) ? this.adminBanks : [];
    },
    atLimit: function () {
      return this.myBanks.length >= this.maxAccounts;
    },
    detail: function () {
      return this.userBankDetail || {};
    },
    currentPrimary: function () {
      const vm = this;
      const found = this.myBanks.filter(function (b) { return vm.isPrimary(b); });
      return found.length ? found[0] : null;
    },
    /** Search across name, country and SWIFT; hide banks already added. */
    matchedBanks: function () {
      const q = String(this.bankSearch || "").toLowerCase().trim();
      if (!q) { return []; }
      const takenIds = {};
      this.myBanks.forEach(function (b) {
        const id = (b.bank && b.bank.id) || b.bank_id;
        if (id) { takenIds[String(id)] = true; }
      });
      const takenNames = {};
      this.myBanks.forEach(function (b) {
        const n = (b.bank && b.bank.bank_name) || "";
        if (n) { takenNames[n.toLowerCase()] = true; }
      });
      return this.allAdminBanks.filter(function (ab) {
        if (!ab) { return false; }
        if (takenIds[String(ab.id)]) { return false; }
        if (takenNames[String(ab.bank_name || "").toLowerCase()]) { return false; }
        const hay = [ab.bank_name, ab.country, ab.swift_code].join(" ").toLowerCase();
        return hay.indexOf(q) !== -1;
      }).slice(0, this.searchLimit);
    },
    detailRows: function () {
      const d = this.detail;
      return [
        { label: "Bank", value: this.bankNameOf(d) },
        { label: "Country", value: d.bank && d.bank.country },
        { label: "Account Holder", value: d.account_holder_name },
        { label: "Account Number", value: d.account_number },
        { label: "Branch Name", value: d.branch_name },
        { label: "Branch Code", value: d.branch_code },
        { label: "Account Type", value: d.bank_type },
      ];
    },
  },
  created() {
    this.load();
  },
  methods: {
    ...mapActions([
      "listAdminBankList", "listMyBanks", "addMyBank", "getMyBank", "removeMyBank",
      "requestAdminBanks",
    ]),
    load() {
      this.isLoading = true;
      const vm = this;
      return Promise.all([this.listMyBanks(), this.listAdminBankList()])
        .then(function () { vm.isLoading = false; })
        .catch(function () { vm.isLoading = false; });
    },
    isPrimary(b) {
      return String(b && b.bank_type || "").toLowerCase() === "primary";
    },
    bankNameOf(b) {
      if (!b) { return "Bank"; }
      return (b.bank && b.bank.bank_name) || b.bank_name || "Bank";
    },
    /** Saved account logo, falling back to the aPay logo when missing/broken. */
    logoOf(b) {
      const l = b && b.bank && b.bank.logo;
      return l || this.fallbackLogo;
    },
    adminLogoOf(ab) {
      return (ab && ab.logo) || this.fallbackLogo;
    },
    onLogoError(e) {
      if (e && e.target && e.target.src !== this.fallbackLogo) {
        e.target.src = this.fallbackLogo;
      }
    },
    maskAccount(n) {
      const s = String(n || "");
      if (s.length <= 4) { return s; }
      return "•••• " + s.slice(-4);
    },
    chooseBank(ab) {
      if (this.atLimit) { return; }
      this.selectedBank = ab;
      this.errors = {};
      this.formError = "";
      this.form = {
        account_holder_name: this.defaultHolderName(),
        account_number: "",
        branch_name: "",
        branch_code: "",
        bank_type: this.myBanks.length === 0 ? "Primary" : "Secondary",
      };
      this.$bvModal.show("bank-add-modal");
    },
    defaultHolderName() {
      const a = this.profile && this.profile.author;
      return (a && a.full_name) ? a.full_name : "";
    },
    validateForm() {
      const e = {};
      if (!this.form.account_holder_name) { e.account_holder_name = "Account holder name is required."; }
      if (!this.form.account_number) { e.account_number = "Account number is required."; }
      this.errors = e;
      return Object.keys(e).length === 0;
    },
    saveBank() {
      this.formError = "";
      if (!this.validateForm()) { return; }
      if (this.atLimit) {
        this.formError = "You can only save " + this.maxAccounts + " bank accounts.";
        return;
      }
      this.isSaving = true;
      const vm = this;
      return this.addMyBank({
        bank: this.selectedBank.id,
        account_number: this.form.account_number,
        bank_type: this.form.bank_type,
        account_holder_name: this.form.account_holder_name,
        branch_name: this.form.branch_name,
        branch_code: this.form.branch_code,
      }).then(function (res) {
        if (res && res.ok) {
          vm.infoText = "Bank account added.";
          vm.$bvModal.hide("bank-add-modal");
          vm.$bvToast.show("bank-toast");
          vm.bankSearch = "";
          return vm.load();
        }
        const fe = (res && res.fieldErrors) || {};
        Object.keys(fe).forEach(function (k) { vm.$set(vm.errors, k, fe[k]); });
        vm.formError = (res && res.message) || "Unable to add this bank account.";
      }).then(function () { vm.isSaving = false; })
        .catch(function () { vm.isSaving = false; });
    },
    openDetail(b) {
      this.detailLoading = true;
      this.$bvModal.show("bank-detail-modal");
      const vm = this;
      return this.getMyBank(b.id)
        .then(function () { vm.detailLoading = false; })
        .catch(function () { vm.detailLoading = false; });
    },
    askRemove(b) {
      this.target = b;
      this.formError = "";
      this.$bvModal.show("bank-remove-modal");
    },
    doRemove() {
      if (!this.target) { return; }
      this.isSaving = true;
      const vm = this;
      return this.removeMyBank(this.target.id).then(function (res) {
        if (res && res.ok) {
          vm.infoText = "Bank account removed.";
          vm.$bvModal.hide("bank-remove-modal");
          vm.$bvToast.show("bank-toast");
          return vm.load();
        }
        vm.formError = (res && res.message) || "Unable to remove this bank account.";
      }).then(function () { vm.isSaving = false; })
        .catch(function () { vm.isSaving = false; });
    },
    askMakePrimary(b) {
      this.target = b;
      this.formError = "";
      this.$bvModal.show("bank-primary-modal");
    },
    /**
     * No update endpoint exists, so a type change is create-then-delete.
     * Create ALWAYS runs first - if it fails nothing is lost.
     */
    /**
     * GET banks returns the nested bank as { logo, bank_name, country } with NO id,
     * but POST banks needs `bank: <id>`. Resolve the id from the admin catalogue by name.
     */
    resolveBankId(bank) {
      if (!bank) { return null; }
      const direct = (bank.bank && bank.bank.id) || bank.bank_id;
      if (direct) { return direct; }
      if (typeof bank.bank === "number" || typeof bank.bank === "string") { return bank.bank; }
      const name = String((bank.bank && bank.bank.bank_name) || "").toLowerCase();
      const country = String((bank.bank && bank.bank.country) || "").toLowerCase();
      if (!name) { return null; }
      const exact = this.allAdminBanks.filter(function (ab) {
        return String(ab.bank_name || "").toLowerCase() === name &&
               (!country || String(ab.country || "").toLowerCase() === country);
      });
      if (exact.length) { return exact[0].id; }
      const byName = this.allAdminBanks.filter(function (ab) {
        return String(ab.bank_name || "").toLowerCase() === name;
      });
      return byName.length ? byName[0].id : null;
    },

    recreateAs(bank, type) {
      const vm = this;
      const bankId = this.resolveBankId(bank);
      if (!bankId) {
        return Promise.resolve({
          ok: false,
          message: "Could not identify this bank in the bank list, so it cannot be re-created. Please remove it and add it again manually.",
        });
      }
      const payload = {
        bank: bankId,
        account_number: bank.account_number,
        bank_type: type,
        account_holder_name: bank.account_holder_name,
        branch_name: bank.branch_name,
        branch_code: bank.branch_code,
      };
      return this.addMyBank(payload).then(function (res) {
        if (!res || !res.ok) {
          return { ok: false, message: (res && res.message) || "Could not re-create the account." };
        }
        return vm.removeMyBank(bank.id).then(function (del) {
          if (!del || !del.ok) {
            return { ok: false, message: "The account was re-created but the old record could not be removed. Please remove it manually." };
          }
          return { ok: true };
        });
      });
    },
    doMakePrimary() {
      if (!this.target) { return; }
      this.isSaving = true;
      const vm = this;
      const oldPrimary = this.currentPrimary;
      return this.recreateAs(this.target, "Primary").then(function (res) {
        if (!res.ok) { vm.formError = res.message; return null; }
        if (oldPrimary && oldPrimary.id !== vm.target.id) {
          return vm.recreateAs(oldPrimary, "Secondary").then(function (r2) {
            if (!r2.ok) { vm.formError = r2.message; }
            return r2;
          });
        }
        return res;
      }).then(function (final) {
        if (final && final.ok) {
          vm.infoText = "Primary account updated.";
          vm.$bvModal.hide("bank-primary-modal");
          vm.$bvToast.show("bank-toast");
        }
        return vm.load();
      }).then(function () { vm.isSaving = false; })
        .catch(function () {
          vm.isSaving = false;
          vm.formError = "Unable to update the primary account.";
        });
    },
    openRequestBank() {
      this.requestForm = { bank_name: "", country: "", swift_code: "", bank_address: "" };
      this.errors = {};
      this.formError = "";
      this.$bvModal.show("bank-request-modal");
    },
    sendBankRequest() {
      const e = {};
      if (!this.requestForm.bank_name) { e.bank_name = "Bank name is required."; }
      if (!this.requestForm.country) { e.country = "Country is required."; }
      this.errors = e;
      this.formError = "";
      if (Object.keys(e).length) { return; }
      this.isSaving = true;
      const vm = this;
      const fd = new FormData();
      Object.keys(this.requestForm).forEach(function (k) {
        fd.append(k, vm.requestForm[k]);
      });
      return this.$store.dispatch("requestAdminBanks", fd).then(function () {
        vm.infoText = "Your bank request has been submitted for review.";
        vm.$bvModal.hide("bank-request-modal");
        vm.$bvToast.show("bank-toast");
      }).catch(function () {
        vm.formError = "Unable to submit the request.";
      }).then(function () { vm.isSaving = false; });
    },
  },
};
</script>

<style lang="scss" scoped>
.bank-info-card { min-height: 420px; }
.section-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.section-head h5 { margin: 0; font-size: 16px; font-weight: 600; }
.count-pill {
  font-size: 12px; font-weight: 600; padding: 2px 10px; border-radius: 10px;
  background: #eef1f4; color: #55606a;
}
.count-pill.is-full { background: #fdf3e0; color: #e08b00; }
.empty-box {
  background: #f7f9fa; border: 1px dashed #dfe3e8; border-radius: 8px;
  padding: 14px; color: #6c757d; font-size: 13px;
}
.empty-box.small { padding: 10px; }
.bank-row {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 4px; border-bottom: 1px solid #f1f3f5;
}
.bank-row:last-child { border-bottom: none; }
.bank-logo {
  width: 42px; height: 42px; object-fit: contain; border-radius: 6px;
  background: #fff; border: 1px solid #eef1f4; padding: 3px; flex: 0 0 auto;
}
.bank-logo.sm { width: 34px; height: 34px; }
.bank-main { flex: 1; min-width: 0; }
.bank-name { font-weight: 600; font-size: 14px; }
.bank-sub { font-size: 12px; color: #6c757d; }
.type-pill {
  font-size: 10px; padding: 1px 8px; border-radius: 9px; margin-left: 6px; font-weight: 700;
}
.type-pill.is-primary { background: #e3f0fb; color: #0066b3; }
.type-pill.is-secondary { background: #eef1f4; color: #6c757d; }
.bank-actions { display: flex; gap: 10px; flex: 0 0 auto; }
.act { font-size: 12px; font-weight: 600; color: #0066b3; white-space: nowrap; }
.act.danger { color: #dc3545; }
.search-results { border: 1px solid #eef1f4; border-radius: 8px; margin-top: 8px; }
.result-row {
  display: flex; align-items: center; gap: 10px; padding: 10px 12px;
  border-bottom: 1px solid #f4f6f8; cursor: pointer;
}
.result-row:last-child { border-bottom: none; }
.result-row:hover { background: #f7fafd; }
.recipient-box {
  display: flex; align-items: center; gap: 12px;
  background: #f6f8fa; border-radius: 8px; padding: 12px 14px; margin-bottom: 14px;
}
.form-row-x { margin-bottom: 14px; }
.form-row-x label { display: block; font-size: 13px; color: #6c757d; margin-bottom: 5px; }
.text-danger { color: #dc3545; font-size: 12px; display: block; margin-top: 4px; }
.muted { color: #6c757d; }
.small { font-size: 12px; }
.detail-row {
  display: flex; justify-content: space-between; padding: 8px 2px;
  border-bottom: 1px solid #f2f4f6; font-size: 14px; gap: 16px;
}
.detail-row:last-child { border-bottom: none; }
.detail-row span { color: #6c757d; }
</style>
